import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { LanguageService } from '../../../services/i18n/language';
import { CustomsCalculationsService, CustomsCalculation } from '../../../services/api/customs-calculations';
import { AuthService } from '../../../services/auth/auth';
import { Subscription } from 'rxjs';
import { ViewCalculationDetailsComponent } from '../../shared/customs-calculations/view-calculation-details/view-calculation-details';
import { SupplierDocumentComponent } from '../../shared/supplier-document/supplier-document';
import { ContractUploadComponent } from '../../shared/contract-upload/contract-upload';

/**
 * Личный кабинет поставщика (ROLE_SUPPLIER). Пока одна вкладка «Поставщик» —
 * список перевозок, к которым поставщика привязал грузовладелец (по приглашению),
 * с тем же предпросмотром расчёта, что видит грузовладелец во вкладке
 * «Предварительный расчёт».
 */
@Component({
  selector: 'app-supplier-dashboard',
  standalone: true,
  imports: [CommonModule, DatePipe, ViewCalculationDetailsComponent, SupplierDocumentComponent, ContractUploadComponent],
  templateUrl: './supplier-dashboard.html',
  styleUrl: './supplier-dashboard.scss'
})
export class SupplierDashboardComponent implements OnInit, OnDestroy {
  calculations: CustomsCalculation[] = [];
  selectedCalculation: CustomsCalculation | null = null;
  loading = false;
  error: string | null = null;

  titleText = 'Кабинет поставщика';
  tabText = 'Поставщик';
  loadingText = 'Загрузка...';
  emptyStateText = 'Пока нет перевозок, к которым вас привязали как поставщика.';
  columnNumberText = '№';
  columnRouteText = 'Маршрут';
  columnCreatedText = 'Создана';
  openText = 'Открыть';
  backToListText = '← К списку поставок';

  private subscriptions = new Subscription();

  constructor(
    private languageService: LanguageService,
    private customsCalculationsService: CustomsCalculationsService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;

    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/login']);
      return;
    }

    const user = this.authService.getUser();
    if (user?.role?.code !== 'ROLE_SUPPLIER') {
      this.router.navigate(['/']);
      return;
    }

    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );

    this.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private updateTranslations(): void {
    this.titleText = this.t('supplierDashboard.title', 'Кабинет поставщика');
    this.tabText = this.t('supplierDashboard.tab', 'Поставщик');
    this.loadingText = this.t('supplierDashboard.loading', 'Загрузка...');
    this.emptyStateText = this.t('supplierDashboard.emptyState', 'Пока нет перевозок, к которым вас привязали как поставщика.');
    this.columnNumberText = this.t('supplierDashboard.columnNumber', '№');
    this.columnRouteText = this.t('supplierDashboard.columnRoute', 'Маршрут');
    this.columnCreatedText = this.t('supplierDashboard.columnCreated', 'Создана');
    this.openText = this.t('supplierDashboard.open', 'Открыть');
    this.backToListText = this.t('supplierDashboard.backToList', '← К списку поставок');
  }

  private load(): void {
    this.loading = true;
    this.error = null;

    this.customsCalculationsService.getMyCalculationsAsSupplier().subscribe({
      next: (calculations) => {
        this.calculations = calculations;
        // Если поставку только одну — сразу открываем её, выбирать не из чего.
        this.selectedCalculation = calculations.length === 1 ? calculations[0] : null;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading supplier calculations:', err);
        this.error = this.t('supplierDashboard.errorLoad', 'Ошибка при загрузке перевозок');
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  select(calculation: CustomsCalculation): void {
    this.selectedCalculation = calculation;
  }

  backToList(): void {
    this.selectedCalculation = null;
  }
}
